import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../App';

// Generování časů od 04:00 do 22:00 po 15 minutách
const generujCasy = () => {
  const casy = [];
  for (let h = 4; h <= 22; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 22 && m > 0) break;
      casy.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    }
  }
  return casy;
};

const CASY = generujCasy();

const casNaMinuty = (cas) => {
  const [h, m] = cas.split(':').map(Number);
  return h * 60 + m;
};

const vypocitejHodiny = (od, do_, obedZaskrtnuto) => {
  if (!od || !do_) return null;
  const minuty = casNaMinuty(do_) - casNaMinuty(od);
  if (minuty <= 0) return null;
  const hodiny = minuty / 60;
  if (obedZaskrtnuto && hodiny >= 6) return hodiny - 0.5;
  return hodiny;
};

export default function NovyVykaz({ onClose, onSaved }) {
  const { profile: user } = useAuth();
  const [zakazky, setZakazky] = useState([]);
  const [form, setForm] = useState({
    datum: new Date().toISOString().split('T')[0],
    od: '08:00',
    do_: '16:00',
    obed: true,
    zakazka_id: '',
    poznamka: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('zakazky').select('*').eq('aktivni', true).eq('uzavrena', false).order('nazev')
      .then(({ data }) => setZakazky(data || []));
  }, []);

  const hodiny = useMemo(() => vypocitejHodiny(form.od, form.do_, form.obed), [form.od, form.do_, form.obed]);

  const handleSave = async () => {
    if (!form.zakazka_id || !form.datum) {
      setError('Vyplňte datum a zakázku.');
      return;
    }
    if (!hodiny || hodiny <= 0) {
      setError('Čas „Do" musí být pozdější než čas „Od".');
      return;
    }

    setSaving(true);
    setError('');

    const d = new Date(form.datum);
    const { data: uzavreno } = await supabase
      .from('uzavrene_mesice')
      .select('id')
      .eq('rok', d.getFullYear())
      .eq('mesic', d.getMonth() + 1)
      .maybeSingle();

    if (uzavreno) {
      setError('Tento měsíc je uzavřen, nelze přidat výkaz.');
      setSaving(false);
      return;
    }

    const { error: err } = await supabase.from('vykazy').insert({
      zamestnanec_id: user.id,
      zakazka_id: form.zakazka_id,
      datum: form.datum,
      cas_od: form.od,
      cas_do: form.do_,
      hodiny: hodiny,
      poznamka: form.poznamka || null,
      stav: 'čeká na schválení'
    });

    if (err) { setError('Chyba při ukládání: ' + err.message); setSaving(false); return; }
    onSaved();
  };

  const selectStyle = { border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 15, fontFamily: 'DM Sans, sans-serif', outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 24, color: '#111' }}>Nový výkaz práce</h3>

        {error && <div style={{ background: '#fde8e6', color: '#c0392b', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="form-group">
            <label className="form-label">Datum</label>
            <input className="form-input" type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Od</label>
              <select style={selectStyle} value={form.od} onChange={e => setForm({...form, od: e.target.value})}>
                {CASY.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Do</label>
              <select style={selectStyle} value={form.do_} onChange={e => setForm({...form, do_: e.target.value})}>
                {CASY.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#333', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={form.obed}
              onChange={e => setForm({...form, obed: e.target.checked})}
              style={{ width: 16, height: 16, accentColor: '#2d6a4f', cursor: 'pointer' }}
            />
            Odečíst přestávku na oběd (30 min)
            <span style={{ fontSize: 12, color: '#aaa' }}>– jen při 6+ hod</span>
          </label>

          <div style={{ background: hodiny && hodiny > 0 ? '#d8f3dc' : '#f0f0f0', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#555', fontWeight: 600 }}>Celkem hodin:</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: hodiny && hodiny > 0 ? '#1b4332' : '#aaa' }}>
              {hodiny && hodiny > 0 ? `${hodiny} h` : '—'}
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Zakázka</label>
            <select className="form-input" value={form.zakazka_id} onChange={e => setForm({...form, zakazka_id: e.target.value})}>
              <option value="">Vyberte zakázku…</option>
              {zakazky.map(z => <option key={z.id} value={z.id}>{z.nazev}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Poznámka (volitelné)</label>
            <textarea className="form-input" rows={2} value={form.poznamka} onChange={e => setForm({...form, poznamka: e.target.value})} placeholder="Co jste dělali…" style={{ resize: 'none' }} />
          </div>

        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>Zrušit</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || !hodiny || hodiny <= 0} style={{ flex: 2, opacity: (saving || !hodiny || hodiny <= 0) ? 0.7 : 1 }}>
            {saving ? 'Ukládám…' : 'Odeslat ke schválení'}
          </button>
        </div>
      </div>
    </div>
  );
}
